import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';

/**
 * Defines the structure for a line to be rendered in the template.
 * - 'keyValue': A standard line with a key and potentially a value.
 * - 'ellipsis': A special line indicating that the JSON has been truncated.
 */
export interface DisplayLine {
  type: 'keyValue' | 'ellipsis';
  key?: string;
  value?: any;
  depth: number; // For indentation
  isObject: boolean;
};

@Component({
  selector: 'rappider-json-viewer',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './json-viewer.component.html',
  styleUrls: ['./json-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RappiderJsonViewerComponent implements OnChanges {

  /**
   * Accepts a JSON object or array as input.
   */
  @Input() data: any;
  /**
   * The total maximum number of lines (keys) to display across the entire object,
   * including all nested levels. If exceeded, an ellipsis "..." is shown.
   */
  @Input() maxLineToDisplay?: number;

  /**
   * The processed, flat array of lines to be rendered by the template.
   * @internal
   */
  displayLines: DisplayLine[] = [];

  /**
   * Detects changes to inputs and re-processes the data for display.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] || changes['maxLineToDisplay']) {
      this.displayLines = this.flattenObject(this.data, this.maxLineToDisplay);
    }
  }

  /**
   * Checks if a value is a non-null object or array.
   */
  private isObject(value: any): boolean {
    return typeof value === 'object' && value !== null;
  }

  /**
   * Traverses a JSON object recursively and flattens it into an array of DisplayLine objects,
   * respecting the line limit.
   * @param obj The object/array to flatten.
   * @param limit The maximum number of lines to produce.
   * @returns An array of lines for rendering.
   */
  private flattenObject(obj: any, limit?: number): DisplayLine[] {
    const lines: DisplayLine[] = [];
    let lineCount = 0;
    let limitReached = false;

    // Inner recursive function to perform the traversal
    const traverse = (currentObj: any, depth: number) => {
      // Stop if we've already hit the limit or the value is not an object
      if (limitReached || !this.isObject(currentObj)) {
        return;
      }

      // Iterate over keys of the object or array
      for (const key in currentObj) {
        if (Object.prototype.hasOwnProperty.call(currentObj, key)) {

          // Check if the line limit has been reached
          if (limit != null && lineCount >= limit) {
            // Add the ellipsis line only once
            if (!limitReached) {
              lines.push({ type: 'ellipsis', depth: depth, isObject: false });
              limitReached = true;
            }
            return; // Exit traversal
          }

          const value = currentObj[key];
          const isValueObject = this.isObject(value);

          // Add the current key-value pair to our flat list
          lines.push({
            type: 'keyValue',
            key,
            // For objects, the value is not displayed on this line, so we set it to null
            value: isValueObject ? null : value,
            depth,
            isObject: isValueObject,
          });
          lineCount++;

          // If the value is an object, recurse deeper
          if (isValueObject) {
            traverse(value, depth + 1);
            // If the inner traversal hit the limit, stop this loop as well
            if (limitReached) return;
          }
        }
      }
    };

    traverse(obj, 0);
    return lines;
  }
}
